import React, { useState } from 'react';
import { Search, ChevronDown, Filter } from 'lucide-react';

export type SearchField = 'all' | 'order_id' | 'customer_email' | 'customers';

interface EnhancedSearchProps {
  onSearch: (searchTerm: string, searchField: SearchField) => void;
  placeholder?: string;
  className?: string;
}

const EnhancedSearch: React.FC<EnhancedSearchProps> = ({ 
  onSearch, 
  placeholder = "Search orders...",
  className = ""
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedField, setSelectedField] = useState<SearchField>('all');
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);

  const searchFields = [
    { value: 'all', label: 'All', description: 'Search across all fields' },
    { value: 'order_id', label: 'Order ID', description: 'Search by order identifier' },
    { value: 'customer_email', label: 'Customer Email', description: 'Search by customer email' },
    { value: 'customers', label: 'Customers', description: 'Search by customer names' }
  ];

  const handleSearch = () => {
    if (searchTerm.trim()) {
      onSearch(searchTerm.trim(), selectedField);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  const getFieldLabel = (field: SearchField) => {
    return searchFields.find(f => f.value === field)?.label || 'All';
  };

  return (
    <div className={`flex items-center space-x-3 ${className}`}>
      {/* Search Input */}
      <div className="relative flex-1">
        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
          <Search className="h-5 w-5 text-gray-400" />
        </div>
        <input
          type="text"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          onKeyPress={handleKeyPress}
          placeholder={placeholder}
          className="block w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors duration-200"
        />
      </div>

      {/* Field Selector Dropdown */}
      <div className="relative">
        <button
          type="button"
          onClick={() => setIsDropdownOpen(!isDropdownOpen)}
          className="inline-flex items-center justify-between w-48 px-4 py-2.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors duration-200"
        >
          <span className="flex items-center">
            <Filter className="w-4 h-4 mr-2 text-gray-500" />
            {getFieldLabel(selectedField)}
          </span>
          <ChevronDown className={`w-4 h-4 text-gray-500 transition-transform duration-200 ${isDropdownOpen ? 'rotate-180' : ''}`} />
        </button>

        {/* Dropdown Menu */}
        {isDropdownOpen && (
          <>
            {/* Backdrop */}
            <div 
              className="fixed inset-0 z-10" 
              onClick={() => setIsDropdownOpen(false)}
            />
            
            {/* Dropdown Content */}
            <div className="absolute right-0 mt-2 w-64 bg-white border border-gray-200 rounded-lg shadow-lg z-20">
              <div className="py-2">
                {searchFields.map((field) => (
                  <button
                    key={field.value}
                    onClick={() => {
                      setSelectedField(field.value as SearchField);
                      setIsDropdownOpen(false);
                    }}
                    className={`w-full text-left px-4 py-3 text-sm hover:bg-gray-50 transition-colors duration-150 ${
                      selectedField === field.value 
                        ? 'bg-blue-50 text-blue-700 border-r-2 border-blue-500' 
                        : 'text-gray-700'
                    }`}
                  >
                    <div className="font-medium">{field.label}</div>
                    <div className="text-xs text-gray-500 mt-1">{field.description}</div>
                  </button>
                ))}
              </div>
            </div>
          </>
        )}
      </div>

      {/* Search Button */}
      <button
        onClick={handleSearch}
        disabled={!searchTerm.trim()}
        className="inline-flex items-center px-6 py-2.5 border border-blue-600 text-blue-600 font-medium rounded-lg hover:bg-blue-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200"
      >
        <Search className="w-4 h-4 mr-2" />
        Search orders
      </button>
    </div>
  );
};

export default EnhancedSearch;
